/**
 * LaunchDarkly offline mode utilities for local feature flag testing
 */
import { datadogRum } from '@datadog/browser-rum';
import type { LDEvaluationDetail } from 'launchdarkly-js-client-sdk';

/**
 * Parse a string value into appropriate type
 */
const parseValue = (value: string): unknown => {
  // Handle JSON objects and arrays
  if (value.startsWith('{') || value.startsWith('[')) {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn(`⚠️ Failed to parse JSON value: ${value}`);
      return value;
    }
  }

  // Handle booleans
  if (value === 'true') return true;
  if (value === 'false') return false;

  // Handle numbers
  const numValue = Number(value);
  if (!isNaN(numValue) && value.trim() !== '') {
    return numValue;
  }

  // Return as string
  return value;
};

/**
 * Build local flags object from environment variables.
 * Only includes explicitly set LOCAL_FLAG_* variables.
 *
 * Flag name conversion: `LOCAL_FLAG_my_flag` → `my-flag`
 * (strips prefix, replaces underscores with hyphens, lowercases)
 *
 *This ensures flags work regardless of whether the LaunchDarkly key uses uppercase characters.
 */
export const buildLocalFlagsFromEnv = (): Record<string, unknown> => {
  const flags: Record<string, unknown> = {};

  // Enumerate all environment variables and filter LOCAL_FLAG_* entries
  for (const key in process.env) {
    if (key.startsWith('LOCAL_FLAG_')) {
      const value = process.env[key];
      if (value !== undefined) {
        // Convert LOCAL_FLAG_my_flag to my-flag
        const rawFlagName = key.replace('LOCAL_FLAG_', '').replace(/_/g, '-');
        const flagName = rawFlagName.toLowerCase();
        const parsedValue = parseValue(value);

        flags[flagName] = parsedValue;

        // Also store under original casing if different, to support case-sensitive flag keys
        if (rawFlagName !== flagName) {
          flags[rawFlagName] = parsedValue;
        }
      }
    }
  }

  return flags;
};

// Create local feature flags object - only contains explicit overrides
export const createLocalFeatureFlags = (): Record<string, unknown> => {
  // Add metadata ($flagsState, $valid) to prevent console warnings about missing metadata
  return { ...buildLocalFlagsFromEnv(), $flagsState: {}, $valid: true };
};

// LaunchDarkly configuration with offline mode support
export const createLaunchDarklyOptions = (clientSideId: string) => {
  if (process.env.USE_LOCAL_FEATURE_FLAGS === 'true') {
    return {
      clientSideID: 'offline',
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
      // bootstrap with local flags and disable all network calls
      options: {
        bootstrap: createLocalFeatureFlags(),
        streaming: false,
        sendEvents: false,
        diagnosticOptOut: true,
        fetchGoals: false,
      },
    };
  }

  // Online mode
  const safeClientSideId = clientSideId || '';

  // Warn if running in online mode without client ID
  if (!safeClientSideId) {
    console.warn(
      '⚠️ WARNING: LaunchDarkly client-side ID is missing! Feature flags will use defaults.',
    );
  }

  return {
    clientSideID: safeClientSideId,
    timeout: 5000, // Consistent timeout for online mode (5 seconds for production)
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
    options: {
      // Performance and privacy optimizations
      ...(process.env.LAUNCH_DARKLY_BASE_URL
        ? { baseUrl: process.env.LAUNCH_DARKLY_BASE_URL }
        : {}),
      ...(process.env.LAUNCH_DARKLY_STREAM_URL
        ? { streamUrl: process.env.LAUNCH_DARKLY_STREAM_URL }
        : {}),
      ...(process.env.LAUNCH_DARKLY_EVENTS_URL
        ? { eventsUrl: process.env.LAUNCH_DARKLY_EVENTS_URL }
        : {}),
      evaluationReasons: false, // Reduce tracking data collection
      inspectors: [
        {
          type: 'flag-used' as const,
          name: 'dd-inspector',
          method: (key: string, detail: LDEvaluationDetail) => {
            datadogRum.addFeatureFlagEvaluation(key, detail.value);
          },
        },
      ],
    },
    context: {
      // Initialize with a non-anonymous system context to avoid anonymous sessions
      kind: 'system',
      key: 'topstep-trm-frontend-service',
      anonymous: false,
    },
  };
};

// Console logging for offline mode status
export const logLaunchDarklyMode = () => {
  console.log(
    process.env.USE_LOCAL_FEATURE_FLAGS === 'true'
      ? '🏠 Frontend LaunchDarkly running in OFFLINE mode with local flags'
      : '🌐 Frontend LaunchDarkly running in ONLINE mode',
  );
};
